import React, { useState } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2, AlertTriangle } from 'lucide-react';
import {
    stcBinCategoryService,
    StcBinCategoryDto
} from '../../../../../services/stcBinCategoryService';

interface BinCategoryBulkUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedIds: number[];
    data: StcBinCategoryDto[];
    onUpdateSuccess: () => void;
    onDeleteSuccess: () => void;
}

interface UpdateFields {
    stcBinCatNo: boolean;
    lng_1: boolean;
    lng_2: boolean;
    lng_3: boolean;
}

export const BinCategoryBulkUpdateModal: React.FC<BinCategoryBulkUpdateModalProps> = ({
    isOpen,
    onClose,
    selectedIds,
    data,
    onUpdateSuccess,
    onDeleteSuccess
}) => {
    const [activeTab, setActiveTab] = useState<'edit' | 'delete'>('edit');

    const selectedItems = data.filter(item => selectedIds.includes(item.id));
    const selectedCount = selectedItems.length;
    const isSingleSelection = selectedCount === 1;
    const singleItem = isSingleSelection ? selectedItems[0] : null;

    // Edit state - pre-fill when single selection
    const [updateFields, setUpdateFields] = useState<UpdateFields>({
        stcBinCatNo: isSingleSelection,
        lng_1: isSingleSelection,
        lng_2: isSingleSelection,
        lng_3: isSingleSelection
    });
    const [stcBinCatNo, setStcBinCatNo] = useState<number>(singleItem?.stcBinCatNo ?? 0);
    const [lng_1, setLng1] = useState(singleItem?.lng_1 ?? '');
    const [lng_2, setLng2] = useState(singleItem?.lng_2 ?? '');
    const [lng_3, setLng3] = useState(singleItem?.lng_3 ?? '');

    // Operation state
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const enabledFieldCount = Object.values(updateFields).filter(Boolean).length;

    const handleBulkUpdate = async () => {
        try {
            setIsSaving(true);
            setError('');

            if (enabledFieldCount === 0) {
                setError('Velg minst ett felt å oppdatere');
                return;
            }

            if (updateFields.stcBinCatNo && selectedCount > 1) {
                setError('Kategori nr. kan kun endres for én kategori om gangen');
                return;
            }

            // Update each selected item individually
            const updatePromises = selectedItems.map(item => {
                const updateRequest: any = {
                    lng_1: updateFields.lng_1 ? lng_1 : item.lng_1,
                    lng_2: updateFields.lng_2 ? lng_2 : item.lng_2,
                    lng_3: updateFields.lng_3 ? lng_3 : item.lng_3
                };

                return stcBinCategoryService.update(
                    item.companyNo,
                    item.stcBinCatNo,
                    updateRequest
                );
            });

            await Promise.all(updatePromises);

            setShowSuccess(true);
            setShowUpdateConfirm(false);
            setTimeout(() => {
                setShowSuccess(false);
                onUpdateSuccess();
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Feil ved oppdatering');
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkDelete = async () => {
        try {
            setIsDeleting(true);
            setError('');

            const deletePromises = selectedItems.map(item =>
                stcBinCategoryService.delete(item.companyNo, item.stcBinCatNo)
            );
            await Promise.all(deletePromises);

            setShowSuccess(true);
            setShowDeleteConfirm(false);
            setTimeout(() => {
                setShowSuccess(false);
                onDeleteSuccess();
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Feil ved sletting');
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    const renderFieldEditor = (
        fieldKey: keyof UpdateFields,
        label: string,
        value: string,
        onChange: (val: string) => void,
        placeholder: string,
        singleOnly?: boolean
    ) => (
        <div className={`border rounded-lg p-4 transition-colors ${
            updateFields[fieldKey] ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
        }`}>
            <div className="flex items-center gap-2 mb-2">
                <input
                    type="checkbox"
                    id={`field-${fieldKey}`}
                    checked={updateFields[fieldKey]}
                    onChange={(e) => setUpdateFields(prev => ({ ...prev, [fieldKey]: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor={`field-${fieldKey}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                    {label}
                </label>
            </div>
            {updateFields[fieldKey] && (
                <div className="mt-2 space-y-2">
                    {singleOnly && selectedCount > 1 && (
                        <div className="bg-amber-50 border border-amber-200 rounded p-2">
                            <p className="text-xs text-amber-700">
                                <AlertTriangle className="w-3 h-3 inline mr-1" />
                                {label} kan kun endres for én kategori om gangen.
                            </p>
                        </div>
                    )}
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={placeholder}
                        disabled={singleOnly && selectedCount > 1}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
                        <div>
                            <h2 className="text-lg font-semibold text-white">Vedlikehold Lokasjonskategorier</h2>
                            <p className="text-sm text-blue-100">
                                {selectedCount} {selectedCount === 1 ? 'kategori' : 'kategorier'} valgt
                            </p>
                        </div>
                        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('edit')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'edit'
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            Rediger
                        </button>
                        <button
                            onClick={() => setActiveTab('delete')}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                activeTab === 'delete'
                                    ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                        >
                            Slett
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-red-800">{error}</p>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    <p className="text-sm text-green-800 font-medium">
                                        {activeTab === 'edit' ? 'Oppdatering fullført!' : 'Sletting fullført!'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'edit' ? (
                            <>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-blue-800">
                                            <p className="font-medium mb-1">Masseoppdatering</p>
                                            <p>Velg feltene du ønsker å oppdatere. Kun avkryssede felt vil bli endret for alle valgte kategorier.</p>
                                        </div>
                                    </div>
                                </div>

                                {renderFieldEditor('lng_1', 'Beskrivelse', lng_1, setLng1, 'Ny verdi for språk 1')}
                                {renderFieldEditor('lng_2', 'Språk 2', lng_2, setLng2, 'Ny verdi for språk 2')}
                                {renderFieldEditor('lng_3', 'Språk 3', lng_3, setLng3, 'Ny verdi for språk 3')}

                                {enabledFieldCount > 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                            <div className="text-sm text-green-800">
                                                <p className="font-medium">Klar til å lagre</p>
                                                <p>{enabledFieldCount} felt vil bli oppdatert for {selectedCount} {selectedCount === 1 ? 'kategori' : 'kategorier'}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showUpdateConfirm && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-amber-800">Bekreft oppdatering</p>
                                                <p className="text-sm text-amber-700 mt-1">
                                                    Er du sikker på at du vil oppdatere {selectedCount} {selectedCount === 1 ? 'kategori' : 'kategorier'}?
                                                </p>
                                                <div className="mt-2 text-xs text-amber-700">
                                                    <p className="font-medium">Berørte kategorier:</p>
                                                    <div className="max-h-20 overflow-y-auto mt-1 space-y-0.5">
                                                        {selectedItems.slice(0, 20).map(item => (
                                                            <div key={item.id}>{item.stcBinCatNo}: {item.lng_1}</div>
                                                        ))}
                                                        {selectedItems.length > 20 && (
                                                            <div className="italic">...og {selectedItems.length - 20} til</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 mt-3">
                                                    <button onClick={handleBulkUpdate} disabled={isSaving} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                                        {isSaving ? 'Lagrer...' : 'Ja, oppdater'}
                                                    </button>
                                                    <button onClick={() => setShowUpdateConfirm(false)} className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                                                        Avbryt
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-red-800">
                                            <p className="font-medium mb-1">Massesletting</p>
                                            <p>Du er i ferd med å slette {selectedCount} {selectedCount === 1 ? 'kategori' : 'kategorier'}. Denne handlingen kan ikke angres.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <h3 className="text-sm font-medium text-gray-900">Kategorier som vil bli slettet:</h3>
                                    <div className="max-h-48 overflow-y-auto space-y-1">
                                        {selectedItems.map(item => (
                                            <div key={item.id} className="text-xs bg-gray-50 p-2 rounded border border-gray-200">
                                                <span className="font-medium">{item.stcBinCatNo}</span>: {item.lng_1}
                                                {item.lng_2 && ` / ${item.lng_2}`}
                                                {item.lng_3 && ` / ${item.lng_3}`}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {showDeleteConfirm && (
                                    <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-medium text-red-800">Er du helt sikker?</p>
                                                <p className="text-sm text-red-700 mt-1">
                                                    Denne handlingen kan ikke angres. {selectedCount} {selectedCount === 1 ? 'kategori' : 'kategorier'} vil bli permanent slettet.
                                                </p>
                                                <div className="flex gap-2 mt-3">
                                                    <button onClick={handleBulkDelete} disabled={isDeleting} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                                                        {isDeleting ? 'Sletter...' : 'Ja, slett permanent'}
                                                    </button>
                                                    <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                                                        Avbryt
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
                        <div className="flex items-center justify-between gap-3">
                            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                Lukk
                            </button>

                            {activeTab === 'edit' ? (
                                <button
                                    onClick={() => setShowUpdateConfirm(true)}
                                    disabled={enabledFieldCount === 0 || isSaving || showUpdateConfirm}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        enabledFieldCount === 0 || isSaving || showUpdateConfirm
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                >
                                    <Save className="w-4 h-4" />
                                    Oppdater {selectedCount} {selectedCount === 1 ? 'kategori' : 'kategorier'}
                                </button>
                            ) : (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isDeleting || showDeleteConfirm}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                        isDeleting || showDeleteConfirm
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-red-600 text-white hover:bg-red-700'
                                    }`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Slett {selectedCount} {selectedCount === 1 ? 'kategori' : 'kategorier'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
